/**
 * Formation anchors — the per-Position normalized home spots. (§8.2)
 *
 * Lives in sim/ (not ai/) because BOTH consumers are same-tick authorities:
 * the AI's Layer-3 formation solver blends these with its influence map, and
 * the rules FSM stages the kickoff shape from them (every restart after a
 * goal/half must restore a recognisable block, Law 8 + presentation). ai/
 * re-exports this table so existing imports keep working.
 *
 * Normalized coordinates:
 *   fx ∈ [-1, +1]  along the team's OWN attack axis: -1 = on top of own goal,
 *                  +1 = on the opponent's goal line.
 *   fz ∈ [-1, +1]  across the pitch: sign is a fixed left/right, resolved to a
 *                  world side per team so both teams mirror correctly.
 */

import type { Position } from './types.js';

export interface Anchor {
  fx: number;
  fz: number;
}

/**
 * Base anchor per position. Wide roles carry a |fz|, central roles sit at 0 and
 * are spread laterally at runtime when several players share a position (two CBs,
 * two STs).
 *
 * ── WHAT THE BASE SHAPE IS, AND WHAT IT IS NOT (2026-07-25) ─────────────────
 *
 * This table is the team's shape AT REST: ball at halfway, nobody in possession,
 * no phase shift applied. It is the neutral MID-BLOCK, and every situational
 * departure from it is added on top by ai/situational-shape.ts (in/out of
 * possession, the ball's third, the counter windows, scoreline + time) and by
 * team-ai.ts's AI_PHASE_SHIFT_* table. Reading a striker anchor and thinking
 * "that's too deep for a striker" is the wrong test — the right test is where he
 * ends up in the FinalThird phase with the possession push applied, which is
 * ~14 m from the opponent's goal line.
 *
 * ── WHY THESE NUMBERS CHANGED ───────────────────────────────────────────────
 *
 * The previous table (CB −0.72 … ST +0.60) was described as "a compact modern
 * block" but was MEASURED, on ai/tactical-metrics.ts's §E.4 soak, as the opposite:
 *
 *     base 4-4-2 anchors          gapDefMid 28.9 m   gapMidFwd 37.3 m   length 69.3 m
 *     …as it played out in a soak  gapDefMid 22.2 m   gapMidFwd 33.5 m   length 63.7 m
 *
 * Real inter-line distances are 10–15 m and a compact block is 35–45 m long. A
 * 37 m hole between the midfield band and the strikers is not a shape at all: the
 * strikers are unreachable by a pass (AI_PASS_MAX_COMFORTABLE_DISTANCE is 30 m),
 * so the midfield can only recycle sideways, which is exactly the behaviour that
 * kept being reported. The lines were pulled together — the DEF band barely moved
 * (a back four's depth is the one thing that was right), the MID band came up
 * ~11 m and the FWD band came down ~28 m. The forwards get that depth back, and
 * more, from R2/R4/R5 of situational-shape.ts the moment the team has the ball.
 *
 * ── DOWNSTREAM CONSUMERS — CHANGE THIS TABLE AND VERIFY BOTH ────────────────
 *
 * 1. rules/fsm.ts stageKickoffShape() stages every restart from these anchors,
 *    clamping fx to KICKOFF_FORWARD_PULLBACK_FX so forwards wait in their own
 *    half (Law 8). Suites: rules/kickoff-staging.test.ts (own-half, centre-circle
 *    and no-overlap assertions), rules/set-piece-staging.test.ts.
 * 2. Half a dozen test harnesses spawn their rosters straight off this table
 *    (ai/tactical-metrics.test.ts, ai/match-metrics.test.ts, sim/shoulder-barge,
 *    sim/anim-integrity, …). Those are spawn conveniences, not assertions about
 *    the numbers.
 */
export const POSITION_ANCHORS: Record<Position, Anchor> = {
  GK: { fx: -0.94, fz: 0 },
  // Back four: essentially unchanged. −0.70 puts a CB ~16 m off his own goal
  // line, −0.60 puts a full-back ~21 m — a real back four's resting depth.
  CB: { fx: -0.7, fz: 0 },
  LB: { fx: -0.6, fz: -0.7 },
  RB: { fx: -0.6, fz: 0.7 },
  LWB: { fx: -0.46, fz: -0.84 },
  RWB: { fx: -0.46, fz: 0.84 },
  // Midfield band, ~17 m ahead of the back four instead of ~29 m.
  CDM: { fx: -0.46, fz: 0 },
  CM: { fx: -0.34, fz: 0 },
  LM: { fx: -0.32, fz: -0.74 },
  RM: { fx: -0.32, fz: 0.74 },
  // Forward band, ~20 m ahead of the midfield instead of ~37 m. At rest a 4-4-2's
  // strikers stand just inside the opponent's half; the possession push, the
  // FinalThird phase shift and the counter surge are what put them in the box.
  CAM: { fx: -0.12, fz: 0 },
  LW: { fx: 0.04, fz: -0.76 },
  RW: { fx: 0.04, fz: 0.76 },
  CF: { fx: 0.02, fz: 0 }, // second striker — a shade off the ST, as before
  ST: { fx: 0.06, fz: 0 },
};
